import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { AppFrame } from '../components/AppNav';
import { Banner, EmptyState, Loading } from '../components/ui';
import { formatDateTime } from '../lib/format';
import { useSession } from '../state/session';

export function Templates() {
  const { orgId } = useSession();
  const [templates, setTemplates] = useState<any[] | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    const data = await api.get(`/organizations/${orgId}/templates`);
    setTemplates(data.templates);
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  if (!templates) return <AppFrame><div className="shell section"><Loading /></div></AppFrame>;

  return (
    <AppFrame>
      <div className="shell section stack">
        <header>
          <h1 style={{ margin: 0 }}>Templates</h1>
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            Reusable documents with recipient roles, merge fields and versioned
            field layouts. Each agreement records the template version it came
            from, so sent terms stay reconstructable.
          </p>
        </header>

        {templates.length === 0 ? (
          <EmptyState title="No templates yet">
            Templates are created from the API today. Create one with{' '}
            <code>POST /v1/templates</code> using a document id, role definitions
            and field placements.
          </EmptyState>
        ) : (
          <div className="list">
            {templates.map((template) => (
              <div className="listitem" key={template.id}>
                <div className="row row--between row--top">
                  <div style={{ minWidth: 0 }}>
                    <p className="listitem__title">{template.name}</p>
                    <div className="listitem__meta">
                      <span>Version {template.current_version}</span>
                      <span>{template.version_count} published</span>
                      <span>Created {formatDateTime(template.created_at)}</span>
                    </div>
                  </div>
                  {template.archived_at && <span className="pill">Archived</span>}
                </div>
                {template.description && (
                  <p className="small muted" style={{ margin: '8px 0 0' }}>
                    {template.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <Banner tone="info" title="How versioning works">
          Publishing an edit creates a new version rather than changing the old
          one. Agreements already sent keep pointing at the version they froze, so
          a later edit never rewrites what someone agreed to.
        </Banner>
      </div>
    </AppFrame>
  );
}
